// UI strings for exercises (lesson checks, free practice, the shared session), Finnish (default)
// and English side by side.
import type { TranslateDirection } from '@valyrio/engine/types';
import { defineArea } from '../area';

export const exercisesFi = {
  title: 'Harjoitukset',
  kinds: {
    translate: 'Käännä',
    fill: 'Täydennä muoto',
    order: 'Järjestä sanat',
    case: 'Mikä sija ja miksi?',
    stress: 'Missä tavussa on paino?',
  } as Record<'translate' | 'fill' | 'order' | 'case' | 'stress', string>,
  dir: {
    'hv-en': 'valyriasta englanniksi',
    'hv-fi': 'valyriasta suomeksi',
    'en-hv': 'englannista valyriaksi',
    'fi-hv': 'suomesta valyriaksi',
  } as Record<TranslateDirection, string>,
  answerLabel: 'Vastauksesi',
  translateLabel: 'Käännöksesi',
  /** The fill's ask line: "sana pryjatys" (the headword set as High Valyrian after it; the form asked is the field's label, formAsk). */
  fillWord: 'sana',
  orderHint: 'Napauta sanat oikeaan järjestykseen. Valitun sanan saat pois napauttamalla sitä uudelleen.',
  orderPicked: 'Valitut sanat',
  orderLeft: 'Jäljellä',
  orderClear: 'Tyhjennä',
  caseQ: (word: string) => `Missä sijassa ${word} on?`,
  stressQ: 'Napauta painollista tavua.',
  check: 'Tarkista',
  next: 'Seuraava',
  finish: 'Näytä tulos',
  expected: 'Odotettu vastaus',
  yours: 'Vastasit',
  verdict: { correct: 'oikein', close: 'melkein', wrong: 'ei aivan' } as Record<'correct' | 'close' | 'wrong', string>,
  progress: (i: number, n: number) => `${i} / ${n}`,
  official: 'virallinen käännös',
  wiki: 'wikin esimerkki',
  answerRequired: 'Vastaa ensin.',
  summary: (right: number, n: number, pct: number) => `${right} / ${n} oikein · ${pct}\u00a0%`,
  scoreNote: 'Melkein oikeasta vastauksesta saa puoli pistettä.',
  // free practice
  freeEmpty: 'Harjoituksia ei vielä ole. Ne kootaan avattujen oppituntien esimerkeistä ja lauseista, joiden kaikki sanat olet jo nähnyt.',
  freeMore: 'Uusi kierros',
  mode: { cards: 'Kortit', exercises: 'Harjoitukset' } as Record<'cards' | 'exercises', string>,
  modeLabel: 'Harjoitustapa',
  sources: (n: number) => `${n} ${n === 1 ? 'lause' : 'lausetta'} käytettävissä`,
  reviewTitle: 'Nämä kannattaa katsoa vielä kerran',
  retryHint: 'Kertaa ensin oppitunnin kohta, jota virheet koskevat. Uusi yritys kysyy osin eri asioita eri järjestyksessä.',
  orderContext: 'Lause on osa repliikkiä:',
  stressCheck: 'Tarkista',
  macrons: {
    label: 'Pituusmerkit vastauksissa',
    note: 'Lempeä',
    strict: 'Tarkka',
  },
};

export const exercisesEn: typeof exercisesFi = {
  title: 'Exercises',
  kinds: {
    translate: 'Translate',
    fill: 'Fill in the form',
    order: 'Put the words in order',
    case: 'Which case, and why?',
    stress: 'Which syllable is stressed?',
  },
  dir: {
    'hv-en': 'Valyrian to English',
    'hv-fi': 'Valyrian to Finnish',
    'en-hv': 'English to Valyrian',
    'fi-hv': 'Finnish to Valyrian',
  },
  answerLabel: 'Your answer',
  translateLabel: 'Your translation',
  fillWord: 'the word',
  orderHint: 'Tap the words in the right order. Tap a chosen word to take it back.',
  orderPicked: 'Chosen words',
  orderLeft: 'Left',
  orderClear: 'Clear',
  caseQ: (word) => `Which case is ${word} in?`,
  stressQ: 'Tap the stressed syllable.',
  check: 'Check',
  next: 'Next',
  finish: 'Show the result',
  expected: 'Expected answer',
  yours: 'You answered',
  verdict: { correct: 'correct', close: 'almost', wrong: 'not right' },
  progress: (i, n) => `${i} / ${n}`,
  official: 'official translation',
  wiki: 'wiki example',
  answerRequired: 'Answer first.',
  summary: (right, n, pct) => `${right} / ${n} correct · ${pct}%`,
  scoreNote: 'An almost-right answer counts as half.',
  freeEmpty: 'No exercises yet. They are built from the examples of unlocked lessons and from sentences whose words you have all met.',
  freeMore: 'New round',
  mode: { cards: 'Cards', exercises: 'Exercises' },
  modeLabel: 'Practice type',
  sources: (n) => `${n} ${n === 1 ? 'source' : 'sources'} available`,
  reviewTitle: 'Worth another look',
  retryHint: 'Go back over the part of the lesson these were about first. A new try asks partly different things, in a different order.',
  orderContext: 'The sentence is part of a line:',
  stressCheck: 'Check',
  macrons: {
    label: 'Macrons in answers',
    note: 'Gentle',
    strict: 'Strict',
  },
};

// Messages.exercises (i18n/area.ts: collected by area-registry.ts)
export const area = defineArea('exercises', exercisesFi, exercisesEn);
declare module '../area' {
  interface AreaMessages extends Record<typeof area.key, typeof exercisesFi> {}
}
